import { useEffect, useState } from 'react';
import type { Tenant } from '../lib/database.types';

/**
 * Bandeau d'installation PWA — porté À L'IDENTIQUE du legacy (Big Burger / Food Kourt).
 * Barre fixe en bas d'écran « Installez {resto} sur votre écran / Accédez à votre cagnotte
 * fidélité en 1 tap », avec détection navigateur (iOS Safari/Chrome/Firefox, Android, desktop)
 * et modale d'instructions. Dismiss mémorisé 7 jours. S'affiche dès qu'il y a un article au
 * panier ou sur les étapes avancées (panier / commande / confirmation).
 */
interface InstallBannerProps {
  tenant: Tenant;
  /** Affiche le bandeau de façon plus insistante (panier non vide / étape avancée). */
  active: boolean;
}

const DISMISS_KEY = 'lilco_pwa_banner_dismissed_at';

interface BIPEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: string }>;
}

export default function InstallBanner({ tenant, active }: InstallBannerProps) {
  const [show, setShow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BIPEvent | null>(null);

  useEffect(() => {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;

    let recentlyDismissed = false;
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < 7 * 24 * 60 * 60 * 1000) recentlyDismissed = true;
      }
    } catch {
      /* localStorage indisponible */
    }
    if (isStandalone || recentlyDismissed) {
      setShow(false);
      return;
    }
    setShow(active);

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BIPEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [active]);

  if (!show) return null;

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isSafariIOS = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isChromeIOS = isIOS && /CriOS/.test(ua);
  const isFirefoxIOS = isIOS && /FxiOS/.test(ua);
  const isOtherIOS = isIOS && !isSafariIOS;

  const close = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  const tryInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === 'accepted') close();
      else setInstallPrompt(null);
      return;
    }
    setModalOpen(true);
  };

  const step = (n: number, content: React.ReactNode) => (
    <li className="relative mb-4 pl-11 leading-relaxed">
      <span
        className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full font-bold text-white"
        style={{ background: 'var(--brand-primary)' }}
      >
        {n}
      </span>
      {content}
    </li>
  );

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-[60] flex items-center gap-3 bg-gradient-to-br from-slate-900 to-slate-800 px-4 py-3.5 text-white shadow-[0_-10px_30px_rgba(0,0,0,.25)]">
        <div className="flex-shrink-0 text-3xl">📱</div>
        <div className="flex-1 text-[13px] leading-tight">
          <strong>Installez {tenant.name} sur votre écran</strong>
          <div className="mt-0.5 opacity-75">Accédez à votre cagnotte fidélité en 1 tap</div>
        </div>
        <button
          onClick={tryInstall}
          className="flex-shrink-0 rounded-[10px] px-4 py-2.5 text-[13px] font-semibold text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          Installer
        </button>
        <button
          onClick={close}
          aria-label="Fermer"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-base text-white/85"
        >
          ✕
        </button>
      </div>

      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/75"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white px-6 pb-9 pt-7"
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute right-3.5 top-3.5 flex h-9 w-9 items-center justify-center rounded-full bg-black/5 text-lg text-gray-700"
            >
              ✕
            </button>
            <div
              className="display-font mb-1.5 text-2xl tracking-wide"
              style={{ color: 'var(--brand-primary)' }}
            >
              {tenant.name.toUpperCase()}
            </div>

            {isOtherIOS && (
              <>
                <h2 className="mb-3.5 text-lg font-bold">Ouvrez ce site dans Safari</h2>
                <div className="mb-4 rounded-2xl border-[1.5px] border-amber-400 bg-amber-50 p-4">
                  <div className="mb-2 font-semibold text-amber-900">
                    ⚠ Sur iPhone, seul Safari permet d'installer l'app
                  </div>
                  <div className="text-sm leading-relaxed text-amber-800">
                    Vous utilisez{' '}
                    <strong>
                      {isChromeIOS ? 'Chrome' : isFirefoxIOS ? 'Firefox' : 'un autre navigateur'}
                    </strong>
                    . C'est une limitation Apple, pas un bug.
                  </div>
                </div>
                <h3 className="mb-2.5 font-bold">Comment faire :</h3>
                <ol className="list-none p-0">
                  {step(1, <>Ouvrez ce site dans <strong>Safari</strong> (icône bleue boussole)</>)}
                  {step(2, <>Tapez le bouton <strong>Partager</strong> en bas de l'écran</>)}
                  {step(3, <>Choisissez <strong>« Sur l'écran d'accueil »</strong></>)}
                </ol>
              </>
            )}

            {isSafariIOS && (
              <>
                <h2 className="mb-4 text-lg font-bold">Ajouter à l'écran d'accueil — iPhone</h2>
                <ol className="list-none p-0">
                  {step(
                    1,
                    <>
                      <strong>Tapez le bouton Partager</strong> en bas de Safari (carré avec flèche
                      vers le haut)
                    </>,
                  )}
                  {step(2, <>Descendez et tapez <strong>« Sur l'écran d'accueil »</strong></>)}
                  {step(3, <>Tapez <strong>« Ajouter »</strong> en haut à droite. C'est fini !</>)}
                </ol>
                <div className="mt-4 rounded-xl bg-emerald-50 p-3.5 text-sm text-emerald-800">
                  ✅ L'icône {tenant.name} apparaîtra avec vos autres apps.
                </div>
              </>
            )}

            {!isIOS && isAndroid && !installPrompt && (
              <>
                <h2 className="mb-3.5 text-lg font-bold">Installation Android</h2>
                <p className="mb-3 text-sm leading-relaxed text-gray-600">
                  Dans Chrome, tapez le menu <strong>⋮</strong> en haut à droite, puis{' '}
                  <strong>« Installer l'application »</strong> ou{' '}
                  <strong>« Ajouter à l'écran d'accueil »</strong>.
                </p>
                <p className="text-[13px] text-gray-500">
                  Samsung Internet : menu ☰ puis « Ajouter la page à ».
                </p>
              </>
            )}

            {!isIOS && !isAndroid && (
              <>
                <h2 className="mb-3.5 text-lg font-bold">Installation sur ordinateur</h2>
                <p className="text-sm leading-relaxed text-gray-600">
                  Dans la barre d'adresse Chrome, cherchez l'icône <strong>+</strong> ou ouvrez le
                  menu ⋮ puis <strong>« Installer {tenant.name} »</strong>.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
